import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContentCopy as ContentCopyIcon, Visibility as VisibilityIcon } from '@mui/icons-material';

import StatusCell from '../../../sharedModules/layout/StatusCell';
import ListPage from '../../../sharedModules/listPage/ListPage';

const useStyles = makeStyles(() => ({
  queryPreview: {
    fontFamily: 'monospace',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
  },
  fullQuery: {
    fontFamily: 'monospace',
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '4px',
    overflowX: 'auto',
  },
}));

const BACKFLOW_CATEGORIES = ['BackflowJobItem UPDATE', 'BackflowLog Insert', 'BackflowJobItem Insert'];

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'backflow':
      return 'primary';
    case 'backflow_system_update':
      return 'secondary';
    default:
      return 'default';
  }
};

const ServmanSqlLogsPage: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const [selectedQuery, setSelectedQuery] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const isChillerModule = location.pathname.includes('/chiller_logs');
  const categoryTitle = isChillerModule ? 'Chiller Logs' : 'Backflow Testing';

  const handleViewQuery = (query: string) => {
    setSelectedQuery(query);
    setOpenDialog(true);
  };

  const handleCopyQuery = (query: string) => {
    navigator.clipboard.writeText(query);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedQuery(null);
  };

  const columns = [
    {
      name: 'ID',
      selector: 'id',
      sortable: true,
      grow: 0.1,
    },
    {
      name: 'Status',
      selector: 'status',
      sortable: true,
      grow: 0.4,
      cell: (row: any) => <StatusCell status={row.status} />,
    },
    {
      name: 'Category',
      selector: 'category',
      sortable: true,
      width: '180px',
      cell: (row: any) => <Chip label={row.category} color={getCategoryColor(row.category)} size="small" />,
    },
    {
      name: 'Order No',
      selector: 'order_no',
      sortable: true,
      width: '120px',
    },
    {
      name: 'SQL Query Preview',
      selector: 'query',
      sortable: false,
      grow: 2,
      cell: (row: any) => <Box className={classes.queryPreview}>{row.query}</Box>,
    },
    {
      name: 'Updated At',
      selector: 'updated_at_with_time',
      sortable: true,
      width: '180px',
    },
    {
      name: 'Actions',
      width: '120px',
      cell: (row: any) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" color="primary" onClick={() => handleViewQuery(row.query)} title="View Full Query">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="secondary" onClick={() => handleCopyQuery(row.query)} title="Copy Query">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const fullQueryDialog = (
    <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
      <DialogTitle>Full SQL Query</DialogTitle>
      <DialogContent>
        <Box className={classes.fullQuery}>{selectedQuery}</Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => selectedQuery && handleCopyQuery(selectedQuery)}>Copy to Clipboard</Button>
        <Button onClick={handleCloseDialog} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <ListPage
      title={categoryTitle}
      subtitle="Servman SQL Logs"
      controller="/servman_sql_logs"
      columns={columns}
      defaultFilters={{ category_in: BACKFLOW_CATEGORIES }}
      defaultSort="created_at desc"
      perPage={50}
      afterComponent={fullQueryDialog}
    />
  );
};

export default ServmanSqlLogsPage;
